import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { CreateNewFolder, Folder as FolderIcon } from "@mui/icons-material";
import AppDialog from "../common/AppDialog";
import type { BackendUser } from "types/interfaces/adminUsers";
import type {
  ConnectFolderCreateResponse,
  ConnectFolderIntegrationMatch,
} from "types/interfaces/filesStorage";
import { useGetBackendUserFoldersQuery, useGetBackendUsersQuery } from "features/adminUsers/adminUsers";
import {
  useConnectFolderCreateMutation,
  useCreateConnectFolderMutation,
  useGetConnectFolderIntegrationsQuery,
  useLazyGetConnectFolderOptionsQuery,
} from "features/fileSystem/filesStorage";

export type ConnectFolderMode = "data_migration" | "source_sftp" | "integration";

interface ConnectFolderDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  forcedUser?: BackendUser | null;
  onConnected?: (created: ConnectFolderCreateResponse, folderName: string) => void;
  onError?: (message: string) => void;
}

function backendUserFromIntegrationMatch(m: ConnectFolderIntegrationMatch): BackendUser {
  return {
    id: m.user_id,
    username: m.owner_username ?? "",
    first_name: "",
    last_name: "",
    client_name: m.client_name ?? null,
    client_id: null,
    folder_count: 0,
    integration_count: 0,
    sftp_count: 0,
    date_joined: "",
    last_login: null,
  };
}

function formatFolderNameForDisplay(s: string) {
  return s
    ? s
        .replace(/_/g, " ")
        .split(/\s+/)
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
        .join(" ")
    : s;
}

/** Top-level folder under users/{userId}/ (vendor bucket name); otherwise last path segment. */
function vendorLabelFromBrowsePath(userId: number | null | undefined, pathNorm: string) {
  const norm = (pathNorm || "").replace(/\/+$/, "");
  if (!norm) return "";
  if (userId != null) {
    const root = `users/${userId}`.replace(/\/+$/, "");
    if (norm === root) return "";
    if (norm.startsWith(`${root}/`)) {
      const rel = norm.slice(root.length + 1);
      return rel.split("/").filter(Boolean)[0] || "";
    }
  }
  return norm.split("/").filter(Boolean).slice(-1)[0] || "";
}

function parseErrorMessage(e: any): string {
  const data = e?.data;
  let msg = data?.error || data?.message;
  if (!msg && data && typeof data === "object") {
    const first = Object.values(data).find((v) => {
      if (typeof v === "string") return true;
      return Array.isArray(v) && v.length > 0 && typeof v[0] === "string";
    }) as string | string[] | undefined;
    if (typeof first === "string") msg = first;
    else if (Array.isArray(first)) msg = first.join(", ");
  }
  if (!msg && typeof e?.error === "string") msg = e.error;
  return msg || "Failed to connect folder. Please check selected path and try again.";
}

export default function ConnectFolderDialog({
  open,
  onClose,
  title = "Connect folder",
  forcedUser = null,
  onConnected,
  onError,
}: ConnectFolderDialogProps) {
  const forcedUserId = forcedUser?.id ?? null;
  const [isDialogReady, setIsDialogReady] = useState(false);
  const [connectInProgress, setConnectInProgress] = useState(false);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");
  const [connectUserSearch, setConnectUserSearch] = useState("");
  const [connectSelectedUser, setConnectSelectedUser] = useState<BackendUser | null>(forcedUser);
  const [connectMode, setConnectMode] = useState<ConnectFolderMode>("data_migration");
  const [connectSelectedPrefix, setConnectSelectedPrefix] = useState("");
  const [connectBrowsePrefix, setConnectBrowsePrefix] = useState("");
  const [connectBasePrefix, setConnectBasePrefix] = useState("");
  const [connectRootPrefix, setConnectRootPrefix] = useState("");
  const [connectFolderName, setConnectFolderName] = useState("");
  const [connectSelectedConnectionMatch, setConnectSelectedConnectionMatch] =
    useState<ConnectFolderIntegrationMatch | null>(null);
  const prevConnectIntegrationMatchId = useRef<number | null>(null);

  const [triggerConnectOptions, connectOptionsResult] =
    useLazyGetConnectFolderOptionsQuery();
  const [connectFolderCreate] = useConnectFolderCreateMutation();
  const [createConnectFolder, { isLoading: isCreatingConnectFolder }] =
    useCreateConnectFolderMutation();

  const { data: backendUsersData, isFetching: backendUsersFetching } =
    useGetBackendUsersQuery(
      {
        page: 1,
        page_size: 50,
        search: connectUserSearch || undefined,
        sort_by: "name",
        sort_order: "asc",
      },
      { skip: !open || !!forcedUserId },
    );

  const {
    data: connectIntegrationsData,
    isFetching: connectIntegrationsFetching,
  } = useGetConnectFolderIntegrationsQuery(undefined, {
    skip: !open || connectMode !== "integration",
  });

  const integrationsOptions = useMemo(() => {
    const all = connectIntegrationsData?.matches || [];
    if (!forcedUserId) return all;
    return all.filter((m) => m.user_id === forcedUserId);
  }, [connectIntegrationsData?.matches, forcedUserId]);

  const integrationStorageRootPrefix = useMemo(() => {
    if (!connectSelectedUser || !connectSelectedConnectionMatch) return "";
    const v = (
      connectSelectedConnectionMatch.source_connection?.vendor ||
      connectSelectedConnectionMatch.destination_connection?.vendor ||
      ""
    )
      .trim()
      .replace(/[/\\]/g, "_") || "vendor";
    return `users/${connectSelectedUser.id}/${v}`.replace(/\/+$/, "");
  }, [connectSelectedUser?.id, connectSelectedConnectionMatch]);

  const { data: connectOwnedFoldersData } = useGetBackendUserFoldersQuery(
    connectSelectedUser?.id ?? 0,
    {
      skip: !open || !connectSelectedUser || connectMode !== "integration",
    },
  );

  useEffect(() => {
    if (!open) {
      setIsDialogReady(false);
      return;
    }
    setConnectInProgress(false);
    setConnectSelectedUser(forcedUser ?? null);
    setConnectSelectedPrefix("");
    setConnectFolderName("");
    setConnectSelectedConnectionMatch(null);
    setConnectMode("data_migration");
    setConnectUserSearch("");
    setConnectBrowsePrefix("");
    setConnectBasePrefix("");
    setConnectRootPrefix("");
    prevConnectIntegrationMatchId.current = null;
    setNewFolderMode(false);
    setNewFolderName("");
    setNewFolderError("");
    setIsDialogReady(true);
  }, [open, forcedUser]);

  useEffect(() => {
    if (!isDialogReady) return;
    const base = (connectOptionsResult.data?.base_prefix || "").replace(/\/+$/, "");
    if (!open) return;
    if (connectMode === "source_sftp") {
      if (connectBasePrefix !== "") setConnectBasePrefix("");
      if (!connectBrowsePrefix) setConnectBrowsePrefix("");
      return;
    }
    if (!connectSelectedUser) return;
    if (connectMode === "integration" && connectSelectedConnectionMatch) {
      const folders = connectOwnedFoldersData ?? [];
      const existing = folders.find(
        (f) => f.connections_match_id === connectSelectedConnectionMatch.id,
      );
      if (existing?.folder_prefix) return;
    }
    if (base) {
      setConnectBasePrefix(base);
      if (!connectRootPrefix) setConnectRootPrefix(base);
      const browseNorm = (connectBrowsePrefix || "").replace(/\/+$/, "");
      if (!browseNorm || browseNorm === base) {
        setConnectBrowsePrefix(base);
      }
    }
  }, [
    open,
    connectMode,
    connectSelectedUser,
    connectSelectedConnectionMatch,
    connectOptionsResult.data?.base_prefix,
    connectOwnedFoldersData,
    connectBasePrefix,
    connectBrowsePrefix,
    connectRootPrefix,
    isDialogReady,
  ]);

  useEffect(() => {
    if (!isDialogReady) return;
    if (!open) return;
    if (connectMode === "source_sftp") {
      void triggerConnectOptions({
        is_sftp_bucket: true,
        prefix: connectBrowsePrefix || undefined,
      });
      return;
    }
    if (!connectSelectedUser) return;
    void triggerConnectOptions({
      user_id: connectSelectedUser.id,
      is_sftp_bucket: false,
      prefix: connectBrowsePrefix || undefined,
    });
  }, [
    open,
    connectMode,
    connectSelectedUser,
    connectBrowsePrefix,
    triggerConnectOptions,
    isDialogReady,
  ]);

  useEffect(() => {
    if (!isDialogReady) return;
    if (!open || connectMode !== "integration") {
      prevConnectIntegrationMatchId.current = null;
      return;
    }
    if (!connectSelectedUser || !connectSelectedConnectionMatch) {
      prevConnectIntegrationMatchId.current = null;
      return;
    }
    const mid = connectSelectedConnectionMatch.id;
    const matchChanged = prevConnectIntegrationMatchId.current !== mid;
    prevConnectIntegrationMatchId.current = mid;

    const folders = connectOwnedFoldersData ?? [];
    const existing = folders.find((f) => f.connections_match_id === mid);
    if (existing?.folder_prefix) {
      const root = `users/${connectSelectedUser.id}`.replace(/\/+$/, "");
      setConnectRootPrefix(root);
      const p = existing.folder_prefix.replace(/\/+$/, "");
      setConnectBrowsePrefix(p);
      setConnectSelectedPrefix(p);
      setConnectFolderName(connectSelectedConnectionMatch.integration_name);
      return;
    }
    setConnectFolderName(connectSelectedConnectionMatch.integration_name);
    if (matchChanged) {
      const uid = connectSelectedUser.id;
      setConnectRootPrefix(`users/${uid}`.replace(/\/+$/, ""));
      const vRoot = integrationStorageRootPrefix;
      if (vRoot) {
        setConnectBrowsePrefix(vRoot);
        setConnectSelectedPrefix(vRoot);
      } else {
        setConnectBrowsePrefix("");
        setConnectSelectedPrefix("");
      }
    }
  }, [
    open,
    connectMode,
    connectSelectedUser?.id,
    connectSelectedConnectionMatch?.id,
    connectOwnedFoldersData,
    isDialogReady,
    integrationStorageRootPrefix,
  ]);

  const connectRootNorm = (connectRootPrefix || "").replace(/\/+$/, "");
  const connectBrowseNorm = (connectBrowsePrefix || "").replace(/\/+$/, "");
  const isInsideFolder = connectBrowseNorm && connectBrowseNorm !== connectRootNorm;
  const effectiveSelectedPrefix = isInsideFolder
    ? connectBrowseNorm
    : connectSelectedPrefix;
  const rawFolderNameFromPath =
    connectMode === "integration"
      ? ""
      : vendorLabelFromBrowsePath(connectSelectedUser?.id, effectiveSelectedPrefix);
  const suggestedFolderName = formatFolderNameForDisplay(rawFolderNameFromPath);
  const effectiveFolderName = connectFolderName.trim() || suggestedFolderName;

  const refetchConnectOptions = useCallback(() => {
    if (!open || !isDialogReady) return;
    if (connectMode === "source_sftp") {
      void triggerConnectOptions({
        is_sftp_bucket: true,
        prefix: connectBrowsePrefix || undefined,
      });
      return;
    }
    if (!connectSelectedUser) return;
    void triggerConnectOptions({
      user_id: connectSelectedUser.id,
      is_sftp_bucket: false,
      prefix: connectBrowsePrefix || undefined,
    });
  }, [
    open,
    isDialogReady,
    connectMode,
    connectSelectedUser,
    connectBrowsePrefix,
    triggerConnectOptions,
  ]);

  const handleCreateStorageFolder = useCallback(async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    if (/[/\\]/.test(trimmed)) {
      setNewFolderError("Folder name must not contain slashes");
      return;
    }
    const items = connectOptionsResult.data?.items ?? [];
    if (items.some((it) => it.name === trimmed)) {
      setNewFolderError("A folder with this name already exists");
      return;
    }
    const parentPrefix = (connectBrowsePrefix || "").replace(/\/+$/, "");
    try {
      await createConnectFolder({
        is_sftp_bucket: connectMode === "source_sftp",
        prefix: parentPrefix,
        folder_name: trimmed,
      }).unwrap();
      setNewFolderMode(false);
      setNewFolderName("");
      setNewFolderError("");
      refetchConnectOptions();
    } catch {
      setNewFolderError("Failed to create folder");
    }
  }, [
    newFolderName,
    connectOptionsResult.data?.items,
    connectBrowsePrefix,
    createConnectFolder,
    connectMode,
    refetchConnectOptions,
  ]);

  const handleConnectConfirm = async () => {
    if (!connectSelectedUser) return;
    const prefixForConnect =
      connectMode === "integration"
        ? integrationStorageRootPrefix || effectiveSelectedPrefix
        : effectiveSelectedPrefix;
    if (!prefixForConnect) return;
    if (connectMode === "integration" && !connectSelectedConnectionMatch) return;
    const folderName = effectiveFolderName;
    if (!folderName) return;

    setConnectInProgress(true);
    try {
      const created = await connectFolderCreate({
        user_id: connectSelectedUser.id,
        folder: folderName,
        folder_prefix: prefixForConnect,
        is_sftp_bucket: connectMode === "source_sftp",
        is_integration: connectMode === "integration",
        connections_match_id:
          connectMode === "integration"
            ? connectSelectedConnectionMatch?.id
            : undefined,
      }).unwrap();
      onConnected?.(created, folderName);
      onClose();
    } catch (e: any) {
      onError?.(parseErrorMessage(e));
    } finally {
      setConnectInProgress(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={() => {
        if (!connectInProgress) onClose();
      }}
      title={title}
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} disabled={connectInProgress}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConnectConfirm()}
            variant="contained"
            disabled={
              connectInProgress ||
              !connectSelectedUser ||
              (connectMode === "integration"
                ? !integrationStorageRootPrefix && !effectiveSelectedPrefix
                : !effectiveSelectedPrefix) ||
              (connectMode === "integration"
                ? !connectSelectedConnectionMatch
                : !effectiveFolderName)
            }
          >
            {connectInProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Connect"
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Tabs
          value={connectMode}
          onChange={(_e, v) => {
            if (typeof v !== "string") return;
            const next = v as ConnectFolderMode;
            if (next === connectMode) return;
            setConnectMode(next);
            setConnectSelectedUser(forcedUser ?? null);
            setConnectSelectedPrefix("");
            setConnectFolderName("");
            setConnectSelectedConnectionMatch(null);
            setConnectBrowsePrefix("");
            setConnectBasePrefix("");
            setConnectRootPrefix("");
            prevConnectIntegrationMatchId.current = null;
            setNewFolderMode(false);
            setNewFolderName("");
            setNewFolderError("");
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 42,
            "& .MuiTab-root": { textTransform: "none", minHeight: 42, py: 1 },
          }}
        >
          <Tab label="Data Migration" value="data_migration" />
          <Tab label="Source SFTP (integration clients)" value="source_sftp" />
          <Tab label="Integration" value="integration" />
        </Tabs>

        {connectMode === "data_migration" && !forcedUser && (
          <Autocomplete
            options={backendUsersData?.results || []}
            loading={backendUsersFetching}
            value={connectSelectedUser}
            onChange={(_e, v) => {
              setConnectSelectedUser(v);
              setConnectSelectedPrefix("");
              setConnectFolderName("");
              setConnectSelectedConnectionMatch(null);
              setConnectBrowsePrefix("");
              setConnectBasePrefix("");
              setConnectRootPrefix("");
              prevConnectIntegrationMatchId.current = null;
            }}
            getOptionLabel={(u) => `${u.client_name} (id: ${u.id})`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Backend Client"
                onChange={(e) => setConnectUserSearch(e.target.value)}
              />
            )}
          />
        )}

        {connectMode === "integration" && (
          <Autocomplete
            options={integrationsOptions}
            loading={connectIntegrationsFetching}
            value={connectSelectedConnectionMatch}
            isOptionEqualToValue={(option, value) =>
              value ? option.id === value.id : false
            }
            onChange={(_e, v) => {
              setConnectSelectedConnectionMatch(v);
              prevConnectIntegrationMatchId.current = null;
              if (v) {
                setConnectSelectedUser(forcedUser ?? backendUserFromIntegrationMatch(v));
                setConnectFolderName(v.integration_name);
              } else {
                setConnectSelectedUser(forcedUser ?? null);
                setConnectFolderName("");
                setConnectSelectedPrefix("");
                setConnectBrowsePrefix("");
                setConnectBasePrefix("");
                setConnectRootPrefix("");
              }
            }}
            getOptionLabel={(m) =>
              `${m.integration_name} — ${m.client_name} (user id: ${m.user_id}, match id: ${m.id})`
            }
            noOptionsText="No integrations found"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select integration"
                placeholder="Choose an integration"
                helperText={
                  !connectIntegrationsFetching &&
                  !integrationsOptions.length &&
                  connectIntegrationsData !== undefined
                    ? "No integrations in the system"
                    : undefined
                }
              />
            )}
          />
        )}

        {connectMode === "source_sftp" && (
          <Typography variant="body2" color="text.secondary">
            Browse the SFTP bucket, then assign the folder to a backend user below.
          </Typography>
        )}

        {connectMode === "source_sftp" || connectSelectedUser ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Breadcrumbs
                aria-label="connect-folder-breadcrumbs"
                separator="/"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", overflow: "hidden" },
                  "& .MuiBreadcrumbs-li": { minWidth: 0 },
                }}
              >
                <Link
                  underline="hover"
                  color={connectBrowseNorm === connectRootNorm ? "text.primary" : "inherit"}
                  onClick={(e) => {
                    e.preventDefault();
                    setConnectBrowsePrefix(connectRootNorm || "");
                    setConnectSelectedPrefix("");
                    setConnectFolderName(
                      connectMode === "integration"
                        ? connectSelectedConnectionMatch?.integration_name || ""
                        : formatFolderNameForDisplay(
                            vendorLabelFromBrowsePath(
                              connectSelectedUser?.id,
                              connectRootNorm || "",
                            ),
                          ),
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  Root
                </Link>
                {(connectBrowsePrefix || "")
                  .split("/")
                  .filter(Boolean)
                  .map((seg, idx, arr) => {
                    const prefixParts = arr.slice(0, idx + 1).join("/");
                    const isLast = idx === arr.length - 1;
                    return (
                      <Link
                        key={`${prefixParts}-${idx}`}
                        underline="hover"
                        color={isLast ? "text.primary" : "inherit"}
                        onClick={(e) => {
                          e.preventDefault();
                          if (isLast) return;
                          setConnectBrowsePrefix(prefixParts);
                          setConnectSelectedPrefix("");
                          setConnectFolderName(
                            connectMode === "integration"
                              ? connectSelectedConnectionMatch?.integration_name ||
                                  ""
                              : formatFolderNameForDisplay(
                                  vendorLabelFromBrowsePath(
                                    connectSelectedUser?.id,
                                    prefixParts,
                                  ),
                                ),
                          );
                        }}
                        sx={{
                          cursor: isLast ? "default" : "pointer",
                          fontSize: "0.85rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 160,
                        }}
                      >
                        {seg}
                      </Link>
                    );
                  })}
              </Breadcrumbs>
            </Box>

            {connectMode !== "integration" &&
              (connectMode === "source_sftp" || connectSelectedUser) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {!newFolderMode ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CreateNewFolder fontSize="small" />}
                      onClick={() => {
                        setNewFolderMode(true);
                        setNewFolderName("");
                        setNewFolderError("");
                      }}
                      disabled={connectOptionsResult.isFetching}
                      sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    >
                      New folder
                    </Button>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <TextField
                        size="small"
                        label="New folder name"
                        value={newFolderName}
                        onChange={(e) => {
                          setNewFolderName(e.target.value);
                          setNewFolderError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void handleCreateStorageFolder();
                        }}
                        error={!!newFolderError}
                        helperText={newFolderError || undefined}
                        placeholder="Folder name"
                        disabled={isCreatingConnectFolder}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => void handleCreateStorageFolder()}
                          disabled={!newFolderName.trim() || isCreatingConnectFolder}
                          sx={{ textTransform: "none" }}
                        >
                          {isCreatingConnectFolder ? "..." : "Create"}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setNewFolderMode(false);
                            setNewFolderName("");
                            setNewFolderError("");
                          }}
                          disabled={isCreatingConnectFolder}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {connectOptionsResult.isFetching ? (
                <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading folders...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                  {(connectOptionsResult.data?.items || []).length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No subfolders found.
                      </Typography>
                    </Box>
                  ) : (
                    (connectOptionsResult.data?.items || []).map((it) => (
                      <Box
                        key={it.prefix}
                        onClick={() => {
                          const selected = String(it.prefix || "").replace(/\/+$/, "");
                          setConnectBrowsePrefix(selected);
                          setConnectSelectedPrefix(selected);
                          if (connectMode !== "integration") {
                            setConnectFolderName(
                              formatFolderNameForDisplay(
                                vendorLabelFromBrowsePath(connectSelectedUser?.id, selected),
                              ),
                            );
                          } else if (connectSelectedConnectionMatch) {
                            setConnectFolderName(
                              connectSelectedConnectionMatch.integration_name,
                            );
                          }
                        }}
                        sx={{
                          px: 2,
                          py: 1,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <FolderIcon fontSize="small" sx={{ color: "rgb(31,93,107)" }} />
                        <Typography variant="body2">{it.name}</Typography>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {connectMode === "integration"
              ? "Select an integration to browse that client's storage folders."
              : "Select a backend client to browse storage folders."}
          </Typography>
        )}

        {connectMode === "source_sftp" && !forcedUser && (
          <Autocomplete
            options={backendUsersData?.results || []}
            loading={backendUsersFetching}
            value={connectSelectedUser}
            onChange={(_e, v) => {
              setConnectSelectedUser(v);
              setConnectSelectedConnectionMatch(null);
              prevConnectIntegrationMatchId.current = null;
            }}
            getOptionLabel={(u) => `${u.client_name} (id: ${u.id})`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign to backend user"
                onChange={(e) => setConnectUserSearch(e.target.value)}
              />
            )}
          />
        )}

        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          label="Folder name"
          value={connectFolderName || suggestedFolderName}
          onChange={(e) => setConnectFolderName(e.target.value)}
          disabled={!effectiveSelectedPrefix || connectMode === "integration"}
        />
      </Box>
    </AppDialog>
  );
}

